// Realistische Demo-Daten für den Testmodus.
// Alle Daten leben nur im Speicher und werden beim Beenden der Demo verworfen.

import { FEATURE_PLANS } from '@/hooks/useFeatures.constants';

export const DEMO_ORG_ID = 'demo-org-00000000-0000-0000-0000-000000000000';
export const DEMO_USER_ID = 'demo-user-00000000-0000-0000-0000-000000000001';
export const DEMO_PATIENT_PORTAL_USER_ID = 'demo-portal-00000000-0000-0000-0000-000000000002';

export const DEMO_PROFILE = {
  id: 'demo-profile-001',
  user_id: DEMO_USER_ID,
  organization_id: DEMO_ORG_ID,
  first_name: 'Marie',
  last_name: 'Schneider',
  // Demo ist kein Admin-Account → BLS-Import und Leitlinien-Import bleiben verborgen
  role: 'therapeut' as const,
  assistant_name: 'Dädalus',
  dashboard_layout: null,
};

export const DEMO_ORGANIZATION = {
  id: DEMO_ORG_ID,
  name: 'Demo-Praxis Schneider',
  email: 'demo@nutriflow-pro.de',
  setting_type: 'praxis' as const,
  // Nur Praxis-Kernfunktionen – freie Funktionen sind im Profil einsehbar.
  // `ai_native` ist Bestandteil von FEATURE_PLANS.praxis (Basis-Feature).
  enabled_features: [...FEATURE_PLANS.praxis],
};

export const DEMO_ORGANIZATION_KLINIK = {
  id: DEMO_ORG_ID,
  name: 'Demo-Klinik Schneider',
  email: 'demo@nutriflow-pro.de',
  setting_type: 'klinik' as const,
  // Nur Klinik-Kernfunktionen – keine Praxis-spezifischen Features.
  // `ai_native` ist Bestandteil von FEATURE_PLANS.klinik (Basis-Feature).
  enabled_features: [...FEATURE_PLANS.klinik],
};

/**
 * Gibt die passende Demo-Organisation zurück (Praxis oder Klinik).
 * Wird von useFeatures verwendet, um settingType dynamisch zu setzen.
 */
export function getDemoOrganization(settingType: 'praxis' | 'klinik') {
  return settingType === 'klinik' ? DEMO_ORGANIZATION_KLINIK : DEMO_ORGANIZATION;
}

// Inline-Typ, um Circular-Import mit usePortalModules zu vermeiden
export type DemoPortalModule = {
  module_key: string;
  enabled: boolean;
  display_name: string | null;
  sort_order: number;
  config: Record<string, unknown>;
};

/**
 * Alle Portal-Module aktiviert – für die Demo-Ansicht des Patientenportals.
 *
 * @deprecated seit 2026-04-17: Das neue `usePatientFeatures` ersetzt diese
 * Struktur. Vorerst belassen, bis externe Callers migriert sind.
 */
export const DEMO_PORTAL_MODULES_ALL: DemoPortalModule[] = [
  { module_key: 'nutrition',           enabled: true, display_name: 'Tagebuch',    sort_order: 1, config: {} },
  { module_key: 'ai_chat',             enabled: true, display_name: 'Assistent',   sort_order: 2, config: {} },
  { module_key: 'messages',            enabled: true, display_name: 'Nachrichten', sort_order: 3, config: {} },
  { module_key: 'documents',           enabled: true, display_name: 'Dokumente',   sort_order: 4, config: {} },
  { module_key: 'health_integrations', enabled: true, display_name: 'Aktivität',  sort_order: 5, config: {} },
  { module_key: 'lab_results',         enabled: true, display_name: 'Befunde',     sort_order: 6, config: {} },
];

const today = new Date();
const todayStr = today.toISOString().split('T')[0];

function daysFromNow(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().split('T')[0];
}

/**
 * Lokalzeit-ISO-Stempel für Demo-Timestamps. Wichtig: wir setzen die Zeit
 * explizit in der Berliner Zeitzone, damit `berlinDay()` in der Streak-
 * Berechnung die Tage wie erwartet gruppiert.
 */
function demoIso(daysOffset: number, hour = 18, minute = 0): string {
  const d = new Date();
  d.setDate(d.getDate() + daysOffset);
  d.setHours(hour, minute, 0, 0);
  return d.toISOString();
}

export const DEMO_PATIENTS = [
  {
    id: 'demo-patient-001',
    organization_id: DEMO_ORG_ID,
    first_name: 'Anna',
    last_name: 'Müller',
    date_of_birth: '1985-06-15',
    email: 'anna.mueller@demo.de',
    phone: '+49 170 1234567',
    insurance_type: 'GKV',
    insurance_name: 'AOK Bayern',
    diagnosis: 'Adipositas Grad II (BMI 37,2)',
    notes: 'Motivierte Patientin, seit 3 Monaten in Betreuung',
    created_at: '2026-01-15T10:00:00Z',
    is_deleted: false,
    portal_user_id: null,
    portal_invited_at: null,
    status: 'aktiv',
    assigned_therapist_id: null,
  },
  {
    id: 'demo-patient-002',
    organization_id: DEMO_ORG_ID,
    first_name: 'Thomas',
    last_name: 'Weber',
    date_of_birth: '1972-11-03',
    email: 'thomas.weber@demo.de',
    phone: '+49 171 9876543',
    insurance_type: 'PKV',
    insurance_name: 'Debeka',
    diagnosis: 'Diabetes mellitus Typ 2, Hypertonie',
    notes: 'Beruflich stark eingespannt, benötigt flexible Termine',
    created_at: '2026-02-08T14:30:00Z',
    is_deleted: false,
    portal_user_id: null,
    portal_invited_at: null,
    status: 'aktiv',
    assigned_therapist_id: null,
  },
  {
    id: 'demo-patient-003',
    organization_id: DEMO_ORG_ID,
    first_name: 'Lisa',
    last_name: 'Schmidt',
    date_of_birth: '1998-03-22',
    email: 'lisa.schmidt@demo.de',
    phone: '+49 152 5551234',
    insurance_type: 'GKV',
    insurance_name: 'TK',
    diagnosis: 'Anorexia nervosa, Untergewicht (BMI 16,8)',
    notes: 'In Zusammenarbeit mit Psychotherapeut Dr. Hartmann',
    created_at: '2026-03-01T09:00:00Z',
    is_deleted: false,
    portal_user_id: null,
    portal_invited_at: null,
    status: 'aktiv',
    assigned_therapist_id: null,
  },
  {
    id: 'demo-patient-004',
    organization_id: DEMO_ORG_ID,
    first_name: 'Klaus',
    last_name: 'Hoffmann',
    date_of_birth: '1960-08-10',
    email: 'klaus.hoffmann@demo.de',
    phone: '+49 160 7773333',
    insurance_type: 'GKV',
    insurance_name: 'Barmer',
    diagnosis: 'Mangelernährung bei Pankreasinsuffizienz',
    notes: 'Stationär aufgenommen, NRS-2002 Score 4',
    created_at: '2026-03-20T11:00:00Z',
    is_deleted: false,
    portal_user_id: null,
    portal_invited_at: null,
    status: 'aktiv',
    assigned_therapist_id: null,
  },
  {
    id: 'demo-patient-005',
    organization_id: DEMO_ORG_ID,
    first_name: 'Sarah',
    last_name: 'Becker',
    date_of_birth: '1990-12-05',
    email: 'sarah.becker@demo.de',
    phone: '+49 176 4445555',
    insurance_type: 'Selbstzahler',
    insurance_name: null,
    diagnosis: 'Zöliakie, Laktoseintoleranz',
    notes: 'Wünscht glutenfreie Ernährungsplanung',
    created_at: '2026-04-02T08:00:00Z',
    is_deleted: false,
    portal_user_id: null,
    portal_invited_at: null,
    status: 'aktiv',
    assigned_therapist_id: null,
  },
  {
    id: 'demo-patient-006',
    organization_id: DEMO_ORG_ID,
    first_name: 'Max',
    last_name: 'Mustermann',
    date_of_birth: '1978-04-22',
    email: 'max.mustermann@demo.de',
    phone: '+49 151 2223344',
    insurance_type: 'GKV',
    insurance_name: 'TK',
    diagnosis: 'Adipositas Grad I (BMI 32,1), Hypercholesterinämie',
    notes: 'Möchte Ernährungsumstellung, Sport bereits aufgenommen',
    created_at: '2026-04-10T09:30:00Z',
    is_deleted: false,
    portal_user_id: null,
    portal_invited_at: null,
    status: 'aktiv',
    assigned_therapist_id: null,
  },
];

export const DEMO_APPOINTMENTS = [
  {
    id: 'demo-apt-001',
    organization_id: DEMO_ORG_ID,
    patient_id: 'demo-patient-001',
    therapist_id: DEMO_USER_ID,
    date: todayStr,
    time: '09:00',
    end_time: '09:50',
    type: 'Erstberatung',
    status: 'bestaetigt',
    notes: 'Erstgespräch Adipositas-Therapie',
    patient_name: 'Anna Müller',
  },
  {
    id: 'demo-apt-002',
    organization_id: DEMO_ORG_ID,
    patient_id: 'demo-patient-002',
    therapist_id: DEMO_USER_ID,
    date: todayStr,
    time: '10:30',
    end_time: '11:20',
    type: 'Folgeberatung',
    status: 'bestaetigt',
    notes: 'Kontrolle Blutzucker-Tagebuch',
    patient_name: 'Thomas Weber',
  },
  {
    id: 'demo-apt-003',
    organization_id: DEMO_ORG_ID,
    patient_id: 'demo-patient-003',
    therapist_id: DEMO_USER_ID,
    date: todayStr,
    time: '14:00',
    end_time: '14:50',
    type: 'Telemedizin',
    status: 'bestaetigt',
    notes: 'Videokonsultation Gewichtsverlauf',
    patient_name: 'Lisa Schmidt',
  },
  {
    id: 'demo-apt-004',
    organization_id: DEMO_ORG_ID,
    patient_id: 'demo-patient-005',
    therapist_id: DEMO_USER_ID,
    date: daysFromNow(1),
    time: '11:00',
    end_time: '11:50',
    type: 'Erstberatung',
    status: 'bestaetigt',
    notes: 'Zöliakie-Ernährungsberatung',
    patient_name: 'Sarah Becker',
  },
  {
    id: 'demo-apt-005',
    organization_id: DEMO_ORG_ID,
    patient_id: 'demo-patient-004',
    therapist_id: DEMO_USER_ID,
    date: daysFromNow(2),
    time: '08:30',
    end_time: '09:20',
    type: 'Folgeberatung',
    status: 'geplant',
    notes: 'Stationsvisite Ernährungsteam',
    patient_name: 'Klaus Hoffmann',
  },
];

export const DEMO_INVOICES = [
  {
    id: 'demo-inv-001',
    organization_id: DEMO_ORG_ID,
    patient_id: 'demo-patient-001',
    invoice_number: 'RE-2026-001',
    date: '2026-03-15',
    due_date: '2026-04-15',
    status: 'bezahlt',
    total: 85.0,
    items: [{ description: 'Erstberatung Ernährungstherapie (ET1)', amount: 85.0, quantity: 1 }],
    patient_name: 'Anna Müller',
  },
  {
    id: 'demo-inv-002',
    organization_id: DEMO_ORG_ID,
    patient_id: 'demo-patient-002',
    invoice_number: 'RE-2026-002',
    date: '2026-04-01',
    due_date: '2026-05-01',
    status: 'offen',
    total: 65.0,
    items: [{ description: 'Folgeberatung (ET2)', amount: 65.0, quantity: 1 }],
    patient_name: 'Thomas Weber',
  },
];

// ─── Demo-Suggestions für die KI-Companion-Inbox ─────────────────────────────
// Werden vom AiCompanionContext im Demo-Modus statt der DB-Antwort
// ausgeliefert, damit Partner-Demos die KI-Inbox sofort gefüllt sehen.
// Realitätsnahe Werte, aber rein erfunden — KEINE realen Patientendaten.

const demoNowIso = new Date().toISOString();
const demoLastWeekIso = new Date(Date.now() - 5 * 86_400_000).toISOString();

export const DEMO_AI_SUGGESTIONS = [
  {
    id: 'demo-suggestion-001',
    org_id: DEMO_ORG_ID,
    patient_id: 'demo-patient-001',
    kind: 'refeeding_risk',
    priority: 'high',
    payload: {
      risk_level: 'high',
      deterministic_score: 6,
      extreme_markers: [],
      factors: [
        { key: 'bmi_below_16', tier: 'major', label: 'BMI < 16 kg/m²', evidence: 'BMI = 15.4 kg/m²' },
        { key: 'pre_feeding_low_electrolytes', tier: 'major', label: 'Pre-feeding niedrige Elektrolyte', evidence: 'Phosphat 0.72 mmol/l, Kalium 3.3 mmol/l' },
      ],
      rationale:
        'Patient zeigt zwei Major-Kriterien nach NICE CG32 (BMI<16, Hypophosphatämie). Vor Beginn des Kostaufbaus ist eine Phosphat-/Kalium-/Magnesium-Substitution sowie Thiamin-Gabe indiziert. Engmaschige Elektrolyt-Kontrollen in den ersten 7 Tagen.',
      suggested_actions: [
        { kind: 'open_refeeding_dialog', label: 'Refeeding-Plan starten' },
        { kind: 'info', label: 'Thiamin 200–300 mg/d vor Kostaufbau' },
        { kind: 'info', label: 'Elektrolyt-Kontrolle täglich, ersten 7 Tage' },
      ],
    },
    source_run_id: null,
    dedup_key: `refeeding_risk:demo-patient-001:${demoNowIso.slice(0, 10)}`,
    status: 'pending' as const,
    created_at: demoNowIso,
    shown_at: null,
    expires_at: null,
    resolved_at: null,
    resolved_by: null,
    resolved_action: null,
  },
  {
    id: 'demo-suggestion-002',
    org_id: DEMO_ORG_ID,
    patient_id: 'demo-patient-002',
    kind: 'weight_trend',
    priority: 'normal',
    payload: {
      trend_level: 'moderate',
      current_weight_kg: 71.4,
      baseline_weight_kg: 76.1,
      loss_pct_1m: 6.2,
      loss_pct_6m: 8.8,
      rationale:
        'Gewichtsveränderung dokumentiert: 6.2% in 30 Tagen, 8.8% in 6 Monaten. Verlauf in der Akte anzeigen.',
      suggested_actions: [
        { kind: 'open_patient', label: 'Verlauf öffnen' },
        { kind: 'info', label: 'Screening-Dokumentation anzeigen' },
      ],
    },
    source_run_id: null,
    dedup_key: `weight_trend:demo-patient-002:${demoNowIso.slice(0, 10)}`,
    status: 'pending' as const,
    created_at: new Date(Date.now() - 4 * 3600_000).toISOString(),
    shown_at: null,
    expires_at: null,
    resolved_at: null,
    resolved_by: null,
    resolved_action: null,
  },
  {
    id: 'demo-suggestion-003',
    org_id: DEMO_ORG_ID,
    patient_id: 'demo-patient-001',
    kind: 'session_brief',
    priority: 'normal',
    payload: {
      patient_summary:
        'Patientin mit Adipositas Grad II in laufender Verhaltenstherapie. BMI stabil bei 37 seit letzten 4 Wochen. Frühstücks-Routinen schwierig.',
      open_items: [
        'Snack-Strategie zwischen Mittag- und Abendessen',
        'Verlaufs-BMI bei Hausärztin abklären',
      ],
      suggested_topics: [
        {
          title: 'Mahlzeitenstruktur',
          reason: 'Patientin hat im Tagebuch häufig "spontanes Frühstück um 11h" notiert.',
        },
        {
          title: 'Achtsames Essen',
          reason: 'Zwei dokumentierte Hungerattacken am Abend in der Vorwoche.',
        },
      ],
      red_flags: [],
      suggested_actions: [{ kind: 'open_patient', label: 'Patientenakte öffnen' }],
    },
    source_run_id: null,
    dedup_key: 'session_brief:demo-appointment-001',
    status: 'pending' as const,
    created_at: new Date(Date.now() - 90 * 60_000).toISOString(),
    shown_at: null,
    expires_at: null,
    resolved_at: null,
    resolved_by: null,
    resolved_action: null,
  },
  {
    id: 'demo-suggestion-004',
    org_id: DEMO_ORG_ID,
    patient_id: 'demo-patient-003',
    kind: 'drop_out_risk',
    priority: 'normal',
    payload: {
      days_since_last_contact: 22,
      last_appointment_at: new Date(Date.now() - 22 * 86_400_000).toISOString(),
      next_appointment_at: null,
      rationale:
        'Letzter Kontakt vor 22 Tagen, kein Folgetermin geplant. Kontaktpause über 14 Tage dokumentiert.',
      suggested_actions: [
        { kind: 'open_patient', label: 'Patientenakte öffnen' },
        { kind: 'info', label: 'Kontaktverlauf prüfen' },
      ],
    },
    source_run_id: null,
    dedup_key: 'drop_out:demo-patient-003:2026-W20',
    status: 'pending' as const,
    created_at: demoLastWeekIso,
    shown_at: null,
    expires_at: null,
    resolved_at: null,
    resolved_by: null,
    resolved_action: null,
  },
];

// Demo-Patient-Perspektive (für Patientenportal)
export const DEMO_PORTAL_PATIENT = {
  id: 'demo-patient-001',
  organization_id: DEMO_ORG_ID,
  first_name: 'Anna',
  last_name: 'Müller',
  date_of_birth: '1985-06-15',
  email: 'anna.mueller@demo.de',
  portal_user_id: DEMO_PATIENT_PORTAL_USER_ID,
  portal_invited_at: '2026-01-20T10:00:00Z',
  portal_onboarded_at: '2026-01-20T10:05:00Z',
  is_deleted: false,
};

export const DEMO_FOOD_DIARY = [
  {
    id: 'demo-diary-001',
    patient_id: 'demo-patient-001',
    date: todayStr,
    meal_type: 'Frühstück',
    time: '07:30',
    description: 'Vollkornbrot mit Avocado, 1 Ei, Kaffee mit Milch',
    calories: 420,
    protein: 18,
    carbs: 42,
    fat: 22,
  },
  {
    id: 'demo-diary-002',
    patient_id: 'demo-patient-001',
    date: todayStr,
    meal_type: 'Mittagessen',
    time: '12:30',
    description: 'Hähnchenbrust mit Quinoa-Salat und Gemüse',
    calories: 550,
    protein: 38,
    carbs: 48,
    fat: 18,
  },
  {
    id: 'demo-diary-003',
    patient_id: 'demo-patient-001',
    date: daysFromNow(-1),
    meal_type: 'Frühstück',
    time: '08:00',
    description: 'Müsli mit Joghurt und Beeren',
    calories: 380,
    protein: 14,
    carbs: 56,
    fat: 10,
  },
];

export const DEMO_TASKS = [
  {
    id: 'demo-task-001',
    patient_id: 'demo-patient-001',
    title: 'Ernährungstagebuch 3 Tage führen',
    description: 'Bitte dokumentieren Sie 3 Tage lang alle Mahlzeiten mit Uhrzeiten.',
    due_date: daysFromNow(3),
    status: 'offen',
    created_at: todayStr,
  },
  {
    id: 'demo-task-002',
    patient_id: 'demo-patient-001',
    title: 'Blutzucker-Protokoll mitbringen',
    description: 'Zum nächsten Termin bitte das Blutzucker-Protokoll der letzten 2 Wochen mitbringen.',
    due_date: daysFromNow(7),
    status: 'offen',
    created_at: todayStr,
  },
];

export const DEMO_MESSAGES = [
  {
    id: 'demo-msg-001',
    organization_id: DEMO_ORG_ID,
    patient_id: 'demo-patient-001',
    sender_role: 'therapeut',
    content: 'Hallo Frau Müller, wie geht es Ihnen mit dem neuen Ernährungsplan? Gibt es Fragen?',
    created_at: `${daysFromNow(-1)}T14:30:00Z`,
    read_at: `${daysFromNow(-1)}T15:00:00Z`,
  },
  {
    id: 'demo-msg-002',
    organization_id: DEMO_ORG_ID,
    patient_id: 'demo-patient-001',
    sender_role: 'patient',
    content: 'Vielen Dank! Es klappt gut. Ich habe nur eine Frage zu den Zwischenmahlzeiten - darf ich auch Nüsse essen?',
    created_at: `${daysFromNow(-1)}T16:00:00Z`,
    read_at: null,
  },
];

// ────────────────────────────────────────────────────────────────────────────
// Portal-Aktivität — Mood, Reflexion, Nutri-Chat, geplante Check-ins
// ────────────────────────────────────────────────────────────────────────────
//
// Seeds für alle in den Säulen 3–6 eingeführten Tabellen. Sie sorgen dafür,
// dass im Demo-Modus die Portal-UI (Home-Streak, Mood-Chart, Verlauf-Tab,
// Nutri-Chat-Verlauf, Therapeut-Einsicht) realistische Daten zeigt, ohne
// dass Supabase angefasst wird. Die Hooks laden stattdessen diese Konstanten.

/** Mood-Einträge der letzten 14 Tage (mit Varianz + einem leichten Trend). */
export const DEMO_MOOD_ENTRIES = [
  { id: 'demo-mood-014', patient_id: 'demo-patient-001', organization_id: DEMO_ORG_ID,
    score: 3, note: 'Anstrengender Start in die Woche.',              created_at: demoIso(-13, 19, 30) },
  { id: 'demo-mood-013', patient_id: 'demo-patient-001', organization_id: DEMO_ORG_ID,
    score: 2, note: 'Müde, viel Stress im Büro.',                     created_at: demoIso(-12, 20,  5) },
  { id: 'demo-mood-012', patient_id: 'demo-patient-001', organization_id: DEMO_ORG_ID,
    score: 4, note: null,                                             created_at: demoIso(-11, 18, 15) },
  { id: 'demo-mood-011', patient_id: 'demo-patient-001', organization_id: DEMO_ORG_ID,
    score: 3, note: 'Mittag war ok, Abendessen etwas mager.',         created_at: demoIso(-10, 19,  0) },
  { id: 'demo-mood-010', patient_id: 'demo-patient-001', organization_id: DEMO_ORG_ID,
    score: 4, note: 'Spaziergang mit der Familie hat gutgetan.',      created_at: demoIso( -9, 18, 30) },
  { id: 'demo-mood-009', patient_id: 'demo-patient-001', organization_id: DEMO_ORG_ID,
    score: 4, note: null,                                             created_at: demoIso( -8, 19,  0) },
  { id: 'demo-mood-008', patient_id: 'demo-patient-001', organization_id: DEMO_ORG_ID,
    score: 5, note: 'Sehr guter Tag!',                                created_at: demoIso( -7, 20,  0) },
  { id: 'demo-mood-007', patient_id: 'demo-patient-001', organization_id: DEMO_ORG_ID,
    score: 4, note: 'Gut geschlafen.',                                created_at: demoIso( -6, 18, 45) },
  { id: 'demo-mood-006', patient_id: 'demo-patient-001', organization_id: DEMO_ORG_ID,
    score: 3, note: null,                                             created_at: demoIso( -5, 19, 15) },
  { id: 'demo-mood-005', patient_id: 'demo-patient-001', organization_id: DEMO_ORG_ID,
    score: 4, note: 'Tagebuch-Streak hält mich motiviert.',           created_at: demoIso( -4, 18, 50) },
  { id: 'demo-mood-004', patient_id: 'demo-patient-001', organization_id: DEMO_ORG_ID,
    score: 4, note: null,                                             created_at: demoIso( -3, 19,  5) },
  { id: 'demo-mood-003', patient_id: 'demo-patient-001', organization_id: DEMO_ORG_ID,
    score: 5, note: 'Bestform, lange Wanderung.',                     created_at: demoIso( -2, 20, 10) },
  { id: 'demo-mood-002', patient_id: 'demo-patient-001', organization_id: DEMO_ORG_ID,
    score: 4, note: null,                                             created_at: demoIso( -1, 18, 30) },
  { id: 'demo-mood-001', patient_id: 'demo-patient-001', organization_id: DEMO_ORG_ID,
    score: 4, note: 'Heute ist ok.',                                  created_at: demoIso(  0, 18,  0) },
];

/** Reflexions-Einträge — einer pro Prompt, über die letzten zwei Wochen verteilt. */
export const DEMO_REFLECTION_ENTRIES = [
  { id: 'demo-refl-003', patient_id: 'demo-patient-001', organization_id: DEMO_ORG_ID,
    prompt_key: 'weekly_win',
    content: 'Diese Woche habe ich jeden Tag zu Mittag etwas Frisches zubereitet, statt das Fertiggericht aus der Kantine zu nehmen.',
    created_at: demoIso(-12, 17, 20) },
  { id: 'demo-refl-002', patient_id: 'demo-patient-001', organization_id: DEMO_ORG_ID,
    prompt_key: 'challenge',
    content: 'Der Abend war schwierig — ich habe Heißhunger auf Süßes gehabt. Ein Apfel mit Mandelbutter hat geholfen.',
    created_at: demoIso(-6, 21, 10) },
  { id: 'demo-refl-001', patient_id: 'demo-patient-001', organization_id: DEMO_ORG_ID,
    prompt_key: 'grateful',
    content: 'Dankbar, dass ich mich heute nach dem Spaziergang so viel wacher gefühlt habe.',
    created_at: demoIso(-2, 20,  0) },
];

/** Nutri-Chat-Verlauf — User- und Assistant-Turns + ein geplanter Check-in. */
export const DEMO_NUTRI_CHAT_MESSAGES = [
  { id: 'demo-chat-001', patient_id: 'demo-patient-001', organization_id: DEMO_ORG_ID,
    role: 'user',      message_type: 'chat',
    content: 'Hallo Nutri! Was wäre ein gutes Frühstück mit viel Protein?',
    created_at: demoIso(-10,  7, 45) },
  { id: 'demo-chat-002', patient_id: 'demo-patient-001', organization_id: DEMO_ORG_ID,
    role: 'assistant', message_type: 'chat',
    content: 'Guten Morgen, Anna! Griechischer Joghurt (150 g) mit Haferflocken, ein paar Walnüssen und Beeren wäre eine solide Option — rund 20 g Protein. Passt gut zu Ihrem aktuellen Plan.',
    created_at: demoIso(-10,  7, 46) },
  { id: 'demo-chat-003', patient_id: 'demo-patient-001', organization_id: DEMO_ORG_ID,
    role: 'assistant', message_type: 'checkin',
    content: 'Guten Abend, Anna! Nutri schaut kurz vorbei: Wie war Ihr Tag heute auf einer Skala von 1–5? Ein Klick genügt. 🌱',
    created_at: demoIso(-7, 18,  0) },
  { id: 'demo-chat-004', patient_id: 'demo-patient-001', organization_id: DEMO_ORG_ID,
    role: 'user',      message_type: 'chat',
    content: 'Heute war wirklich schön, ich hab eine 5 eingetragen.',
    created_at: demoIso(-7, 18,  7) },
  { id: 'demo-chat-005', patient_id: 'demo-patient-001', organization_id: DEMO_ORG_ID,
    role: 'assistant', message_type: 'chat',
    content: 'Das freut mich zu hören! Ihre aktuelle Serie wird Sie bestimmt motivieren, morgen wieder eine Kleinigkeit zu dokumentieren.',
    created_at: demoIso(-7, 18,  8) },
  { id: 'demo-chat-006', patient_id: 'demo-patient-001', organization_id: DEMO_ORG_ID,
    role: 'user',      message_type: 'chat',
    content: 'Nutri, wie viele kcal hatte mein Abendessen gestern ungefähr?',
    created_at: demoIso(-3, 20, 15) },
  { id: 'demo-chat-007', patient_id: 'demo-patient-001', organization_id: DEMO_ORG_ID,
    role: 'assistant', message_type: 'chat',
    content: 'Gestern Abend haben Sie "Ofengemüse mit Lachs" im Tagebuch — das liegt je nach Portion bei etwa 520–600 kcal. Für den genauen Wert ist ein Blick ins Tagebuch hilfreich.',
    created_at: demoIso(-3, 20, 16) },
];

/** Therapeut-konfigurierte Check-ins. */
export const DEMO_SCHEDULED_CHECKINS = [
  { id: 'demo-checkin-001', patient_id: 'demo-patient-001', organization_id: DEMO_ORG_ID,
    template_key: 'daily_mood', frequency: 'daily', weekday: null, send_hour: 18,
    is_active: true, last_sent_at: demoIso(-1, 18,  0) },
  { id: 'demo-checkin-002', patient_id: 'demo-patient-001', organization_id: DEMO_ORG_ID,
    template_key: 'weekly_reflection', frequency: 'weekly', weekday: 5, send_hour: 17,
    is_active: true, last_sent_at: demoIso(-2, 17,  0) },
];

// ────────────────────────────────────────────────────────────────────────────
// Klinik-Demo-Daten — für Therapeuten-Demo im Klinik-Modus
// ────────────────────────────────────────────────────────────────────────────

export const DEMO_KLINIK_PATIENTS = [
  {
    id: 'demo-klinik-patient-001',
    organization_id: DEMO_ORG_ID,
    first_name: 'Klaus-Peter',
    last_name: 'Vogt',
    date_of_birth: '1951-03-12',
    email: 'k.vogt@demo-klinik.de',
    phone: '+49 89 4445566',
    insurance_type: 'GKV',
    insurance_name: 'AOK Bayern',
    diagnosis: 'Ösophaguskarzinom (C15), präoperativ',
    notes: 'NRS-2002: 4 · GLIM: schwere Mangelernährung · Konsil angefordert',
    created_at: daysFromNow(-12) + 'T08:00:00Z',
    is_deleted: false,
    portal_user_id: null,
    portal_invited_at: null,
    status: 'aktiv',
    assigned_therapist_id: null,
    station: 'Station 4A',
    room_bed: '412B',
    nrs2002_score: 4,
    glim_stage: 'severe' as const,
    admission_type: 'stationär',
    admission_date: daysFromNow(-12),
  },
  {
    id: 'demo-klinik-patient-002',
    organization_id: DEMO_ORG_ID,
    first_name: 'Hannelore',
    last_name: 'Brandt',
    date_of_birth: '1948-07-28',
    email: 'h.brandt@demo-klinik.de',
    phone: '+49 89 7778899',
    insurance_type: 'GKV',
    insurance_name: 'Barmer',
    diagnosis: 'Hüft-TEP re., 2. postoperativer Tag',
    notes: 'NRS-2002: 2 · Kein Konsil · Mobilisierung läuft gut',
    created_at: daysFromNow(-3) + 'T06:30:00Z',
    is_deleted: false,
    portal_user_id: null,
    portal_invited_at: null,
    status: 'aktiv',
    assigned_therapist_id: null,
    station: 'Station 3B',
    room_bed: '318A',
    nrs2002_score: 2,
    glim_stage: 'none' as const,
    admission_type: 'stationär',
    admission_date: daysFromNow(-3),
  },
  {
    id: 'demo-klinik-patient-003',
    organization_id: DEMO_ORG_ID,
    first_name: 'Rudolf',
    last_name: 'Schreiber',
    date_of_birth: '1944-11-05',
    email: 'r.schreiber@demo-klinik.de',
    phone: null,
    insurance_type: 'GKV',
    insurance_name: 'TK',
    diagnosis: 'Ischämischer Schlaganfall (I63.5), Dysphagie',
    notes: 'NRS-2002: 3 · GLIM: moderate Mangelernährung · IDDSI-Level 4',
    created_at: daysFromNow(-8) + 'T11:00:00Z',
    is_deleted: false,
    portal_user_id: null,
    portal_invited_at: null,
    status: 'aktiv',
    assigned_therapist_id: null,
    station: 'Station 4A',
    room_bed: '415A',
    nrs2002_score: 3,
    glim_stage: 'moderate' as const,
    admission_type: 'stationär',
    admission_date: daysFromNow(-8),
  },
  {
    id: 'demo-klinik-patient-004',
    organization_id: DEMO_ORG_ID,
    first_name: 'Ingrid',
    last_name: 'Sommer',
    date_of_birth: '1959-02-14',
    email: 'i.sommer@demo-klinik.de',
    phone: '+49 89 1112233',
    insurance_type: 'GKV',
    insurance_name: 'DAK',
    diagnosis: 'Akute Pankreatitis (K85.9), Tag 3',
    notes: 'NRS-2002: 5 · GLIM: schwere Mangelernährung · NPO, enterale Ernährung geplant',
    created_at: daysFromNow(-3) + 'T14:20:00Z',
    is_deleted: false,
    portal_user_id: null,
    portal_invited_at: null,
    status: 'aktiv',
    assigned_therapist_id: null,
    station: 'Station 2C',
    room_bed: '221B',
    nrs2002_score: 5,
    glim_stage: 'severe' as const,
    admission_type: 'stationär',
    admission_date: daysFromNow(-3),
  },
  {
    id: 'demo-klinik-patient-005',
    organization_id: DEMO_ORG_ID,
    first_name: 'Gerhard',
    last_name: 'Müller',
    date_of_birth: '1956-09-30',
    email: null,
    phone: '+49 89 9998877',
    insurance_type: 'GKV',
    insurance_name: 'IKK',
    diagnosis: 'COPD-Exazerbation (J44.1), NIV-Therapie',
    notes: 'NRS-2002: 3 · GLIM: moderate Mangelernährung · Orales Supplement angepasst',
    created_at: daysFromNow(-5) + 'T09:15:00Z',
    is_deleted: false,
    portal_user_id: null,
    portal_invited_at: null,
    status: 'aktiv',
    assigned_therapist_id: null,
    station: 'Station 3B',
    room_bed: '302A',
    nrs2002_score: 3,
    glim_stage: 'moderate' as const,
    admission_type: 'stationär',
    admission_date: daysFromNow(-5),
  },
  {
    id: 'demo-klinik-patient-006',
    organization_id: DEMO_ORG_ID,
    first_name: 'Bärbel',
    last_name: 'Fischer',
    date_of_birth: '1967-06-19',
    email: null,
    phone: null,
    insurance_type: 'GKV',
    insurance_name: 'AOK',
    diagnosis: 'Sepsis (A41.9), Beatmung Tag 2',
    notes: 'NRS-2002: 6 · GLIM: schwere Mangelernährung · Enterale Ernährung via PEG',
    created_at: daysFromNow(-2) + 'T03:40:00Z',
    is_deleted: false,
    portal_user_id: null,
    portal_invited_at: null,
    status: 'aktiv',
    assigned_therapist_id: null,
    station: 'ICU',
    room_bed: 'ICU-6',
    nrs2002_score: 6,
    glim_stage: 'severe' as const,
    admission_type: 'intensiv',
    admission_date: daysFromNow(-2),
  },
];

export const DEMO_CONSULTS = [
  {
    id: 'demo-konsil-001',
    organization_id: DEMO_ORG_ID,
    patient_id: 'demo-klinik-patient-001',
    requesting_station: 'Station 4A',
    requesting_physician: 'Dr. Hartmann (Onkologie)',
    reason: 'Ernährungsplan vor Chemotherapie — Patient hat in 3 Wochen 8 % KG verloren',
    priority: 'urgent' as const,
    status: 'open' as const,
    assigned_to: null,
    response_note: null,
    created_by: null,
    created_at: demoIso(-1, 9, 30),
    updated_at: demoIso(-1, 9, 30),
    closed_by: null,
    closed_at: null,
    patient_first_name: 'Klaus-Peter',
    patient_last_name: 'Vogt',
    patient_station: 'Station 4A',
    patient_room_bed: '412B',
  },
  {
    id: 'demo-konsil-002',
    organization_id: DEMO_ORG_ID,
    patient_id: 'demo-klinik-patient-003',
    requesting_station: 'Station 4A',
    requesting_physician: 'Dr. Bauer (Neurologie)',
    reason: 'Schluckstörung nach Apoplex — IDDSI-Empfehlung für Kost und Getränke',
    priority: 'urgent' as const,
    status: 'open' as const,
    assigned_to: null,
    response_note: null,
    created_by: null,
    created_at: demoIso(-2, 14, 15),
    updated_at: demoIso(-2, 14, 15),
    closed_by: null,
    closed_at: null,
    patient_first_name: 'Rudolf',
    patient_last_name: 'Schreiber',
    patient_station: 'Station 4A',
    patient_room_bed: '415A',
  },
  {
    id: 'demo-konsil-003',
    organization_id: DEMO_ORG_ID,
    patient_id: 'demo-klinik-patient-004',
    requesting_station: 'Station 2C',
    requesting_physician: 'Dr. Wagner (Innere Medizin)',
    reason: 'Parenterale Ernährung bei Pankreatitis Tag 3 — enterale Ernährung einleiten?',
    priority: 'normal' as const,
    status: 'in_progress' as const,
    assigned_to: DEMO_USER_ID,
    response_note: null,
    created_by: null,
    created_at: demoIso(-3, 11, 0),
    updated_at: demoIso(-1, 16, 30),
    closed_by: null,
    closed_at: null,
    patient_first_name: 'Ingrid',
    patient_last_name: 'Sommer',
    patient_station: 'Station 2C',
    patient_room_bed: '221B',
  },
  {
    id: 'demo-konsil-004',
    organization_id: DEMO_ORG_ID,
    patient_id: 'demo-klinik-patient-006',
    requesting_station: 'ICU',
    requesting_physician: 'Dr. Klein (Intensivmedizin)',
    reason: 'Enterale Ernährung Sepsis-Patient — Ziel-Kalorienmenge und Protokoll festlegen',
    priority: 'urgent' as const,
    status: 'open' as const,
    assigned_to: null,
    response_note: null,
    created_by: null,
    created_at: demoIso(-1, 6, 45),
    updated_at: demoIso(-1, 6, 45),
    closed_by: null,
    closed_at: null,
    patient_first_name: 'Bärbel',
    patient_last_name: 'Fischer',
    patient_station: 'ICU',
    patient_room_bed: 'ICU-6',
  },
  {
    id: 'demo-konsil-005',
    organization_id: DEMO_ORG_ID,
    patient_id: 'demo-klinik-patient-005',
    requesting_station: 'Station 3B',
    requesting_physician: 'Dr. Neumann (Pneumologie)',
    reason: 'Orale Supplementierung COPD — Patient lehnt Trinknahrung ab, Alternativvorschlag',
    priority: 'low' as const,
    status: 'open' as const,
    assigned_to: null,
    response_note: null,
    created_by: null,
    created_at: demoIso(-4, 10, 0),
    updated_at: demoIso(-4, 10, 0),
    closed_by: null,
    closed_at: null,
    patient_first_name: 'Gerhard',
    patient_last_name: 'Müller',
    patient_station: 'Station 3B',
    patient_room_bed: '302A',
  },
];

export function getDemoKlinikData() {
  const patients = DEMO_KLINIK_PATIENTS;
  const consults = DEMO_CONSULTS;
  const screeningQueue = patients.filter(p => p.admission_date >= daysFromNow(-4));
  const malnutritionCases = patients.filter(
    p => p.glim_stage === 'moderate' || p.glim_stage === 'severe',
  );
  return { patients, consults, screeningQueue, malnutritionCases };
}

// StationsUebersicht-Demo-Daten im Shape von StationOverviewRow
export const DEMO_STATION_OVERVIEW = DEMO_KLINIK_PATIENTS.map(p => ({
  patient: {
    id: p.id,
    first_name: p.first_name,
    last_name: p.last_name,
    station: p.station,
    room_bed: p.room_bed,
    admission_date: p.admission_date,
    admission_status: p.admission_type === 'intensiv' ? 'stationaer' : 'stationaer',
    date_of_birth: p.date_of_birth,
  },
  latestNrs: p.nrs2002_score > 0
    ? {
        id: `demo-nrs-${p.id}`,
        at_risk: p.nrs2002_score >= 3,
        total_score: p.nrs2002_score,
        screening_date: p.admission_date,
      }
    : null,
  latestGlim: p.glim_stage !== 'none'
    ? {
        id: `demo-glim-${p.id}`,
        glim_stage: p.glim_stage,
        icd10_code: p.glim_stage === 'severe' ? 'E43' : 'E44',
        assessed_at: p.admission_date + 'T10:00:00Z',
      }
    : null,
}));

// Demo-Ernährungspläne für Klinik (shape: NutritionPlan + patients join)
export const DEMO_GNCP_PLANS = [
  {
    id: 'demo-gncp-klinik-001',
    patient_id: 'demo-klinik-patient-001',
    title: 'Aufbaukost Ösophaguskarzinom präoperativ',
    description: 'Hochkalorische Weichkost; Trinknahrung 3×200 ml/d',
    start_date: daysFromNow(-10),
    end_date: null,
    calories_target: 2200,
    protein_g: 110,
    carbs_g: 250,
    fat_g: 80,
    notes: 'Kein rohes Gemüse; Trinknahrung: Fortimel 3×200 ml/d. Gewichtsmonitoring täglich.',
    status: 'active',
    created_at: daysFromNow(-10) + 'T09:00:00Z',
    patients: { first_name: 'Klaus-Peter', last_name: 'Vogt' },
  },
  {
    id: 'demo-gncp-klinik-002',
    patient_id: 'demo-klinik-patient-003',
    title: 'IDDSI-Kost Dysphagie nach Apoplex',
    description: 'IDDSI Level 4 püriert; Getränke Level 3 angedickt',
    start_date: daysFromNow(-6),
    end_date: null,
    calories_target: 1800,
    protein_g: 75,
    carbs_g: 220,
    fat_g: 65,
    notes: 'Schlucktraining Di/Do. Keine Mischkonsistenzen. Re-Evaluation nach 1 Woche.',
    status: 'active',
    created_at: daysFromNow(-6) + 'T10:00:00Z',
    patients: { first_name: 'Rudolf', last_name: 'Schreiber' },
  },
  {
    id: 'demo-gncp-klinik-003',
    patient_id: 'demo-klinik-patient-004',
    title: 'Enterale Sondenernährung Pankreatitis',
    description: 'Nasojejunale Sonde; Start 10 ml/h, schrittweise Steigerung',
    start_date: daysFromNow(-2),
    end_date: null,
    calories_target: 1600,
    protein_g: 80,
    carbs_g: 200,
    fat_g: 55,
    notes: 'Kein orales Essen. Steigerung 10 ml/h alle 8 h bis Zielrate 67 ml/h. Toleranzcheck täglich.',
    status: 'draft',
    created_at: daysFromNow(-2) + 'T14:00:00Z',
    patients: { first_name: 'Ingrid', last_name: 'Sommer' },
  },
];

// Demo Kostform-Anordnungen für das Menü-Bestellsystem (shape: PatientDietOrderWithPatient)
export const DEMO_DIET_ORDERS = [
  {
    id: 'demo-diet-001',
    organization_id: DEMO_ORG_ID,
    patient_id: 'demo-klinik-patient-001',
    kostform_code: 'sondenkost' as const,
    iddsi_level: null,
    intolerances: [],
    special_notes: 'Trinknahrung 3×200 ml/d zusätzlich; Gewichtskontrolle täglich',
    ordered_at: daysFromNow(-10) + 'T09:00:00Z',
    ordered_by: null,
    created_at: daysFromNow(-10) + 'T09:00:00Z',
    updated_at: daysFromNow(-10) + 'T09:00:00Z',
    patient_first_name: 'Klaus-Peter',
    patient_last_name: 'Vogt',
    patient_station: 'Station 4A',
    patient_room_bed: '412B',
    patient_allergies: [],
  },
  {
    id: 'demo-diet-002',
    organization_id: DEMO_ORG_ID,
    patient_id: 'demo-klinik-patient-002',
    kostform_code: 'leichte_vollkost' as const,
    iddsi_level: null,
    intolerances: ['Laktose'],
    special_notes: 'Post-op; schrittweise Normalisierung',
    ordered_at: daysFromNow(-3) + 'T07:00:00Z',
    ordered_by: null,
    created_at: daysFromNow(-3) + 'T07:00:00Z',
    updated_at: daysFromNow(-3) + 'T07:00:00Z',
    patient_first_name: 'Hannelore',
    patient_last_name: 'Brandt',
    patient_station: 'Station 3B',
    patient_room_bed: '318A',
    patient_allergies: [],
  },
  {
    id: 'demo-diet-003',
    organization_id: DEMO_ORG_ID,
    patient_id: 'demo-klinik-patient-003',
    kostform_code: 'passiert' as const,
    iddsi_level: 4 as const,
    intolerances: [],
    special_notes: 'IDDSI 4; Getränke Level 3 angedickt; keine Mischkonsistenzen',
    ordered_at: daysFromNow(-6) + 'T10:00:00Z',
    ordered_by: null,
    created_at: daysFromNow(-6) + 'T10:00:00Z',
    updated_at: daysFromNow(-6) + 'T10:00:00Z',
    patient_first_name: 'Rudolf',
    patient_last_name: 'Schreiber',
    patient_station: 'Station 4A',
    patient_room_bed: '415A',
    patient_allergies: [],
  },
  {
    id: 'demo-diet-004',
    organization_id: DEMO_ORG_ID,
    patient_id: 'demo-klinik-patient-004',
    kostform_code: 'parenteral' as const,
    iddsi_level: null,
    intolerances: [],
    special_notes: 'Kein orales Essen; NJ-Sonde; Steigerung nach Protokoll',
    ordered_at: daysFromNow(-2) + 'T14:00:00Z',
    ordered_by: null,
    created_at: daysFromNow(-2) + 'T14:00:00Z',
    updated_at: daysFromNow(-2) + 'T14:00:00Z',
    patient_first_name: 'Ingrid',
    patient_last_name: 'Sommer',
    patient_station: 'Station 2C',
    patient_room_bed: '221B',
    patient_allergies: [],
  },
  {
    id: 'demo-diet-005',
    organization_id: DEMO_ORG_ID,
    patient_id: 'demo-klinik-patient-005',
    kostform_code: 'leichte_vollkost' as const,
    iddsi_level: null,
    intolerances: [],
    special_notes: 'Hochkalorische Supplementierung; Fortimel 2×200 ml/d',
    ordered_at: daysFromNow(-5) + 'T08:30:00Z',
    ordered_by: null,
    created_at: daysFromNow(-5) + 'T08:30:00Z',
    updated_at: daysFromNow(-5) + 'T08:30:00Z',
    patient_first_name: 'Gerhard',
    patient_last_name: 'Müller',
    patient_station: 'Station 3B',
    patient_room_bed: '302A',
    patient_allergies: [],
  },
  {
    id: 'demo-diet-006',
    organization_id: DEMO_ORG_ID,
    patient_id: 'demo-klinik-patient-006',
    kostform_code: 'sondenkost' as const,
    iddsi_level: null,
    intolerances: [],
    special_notes: 'Enterale Sondenernährung; Nutrison Energy 1,5 kcal/ml; kontinuierlich 50 ml/h',
    ordered_at: daysFromNow(-1) + 'T06:00:00Z',
    ordered_by: null,
    created_at: daysFromNow(-1) + 'T06:00:00Z',
    updated_at: daysFromNow(-1) + 'T06:00:00Z',
    patient_first_name: 'Bärbel',
    patient_last_name: 'Fischer',
    patient_station: 'ICU',
    patient_room_bed: 'ICU-6',
    patient_allergies: [],
  },
];
